// @ts-check
/** @typedef {import('../types/api').Order} Order */
/** @typedef {import('../types/api').OrderTransaction} OrderTransaction */
/** @typedef {import('../types/api').OrderEvent} OrderEvent */

import {
  AlertCircle,
  AlertTriangle,
  Ban,
  CheckCheck,
  CheckCircle,
  Clock,
  CreditCard,
  FileText,
  IndianRupee,
  Package,
  Pencil,
  RefreshCw,
  RotateCcw,
  ShoppingBag,
  Trash2,
  Truck,
  User,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminInventoryService from '../services/adminInventoryService';
import adminOrderService from '../services/adminOrderService';
import { cancelOrderPlan } from '../utils/orderCancel';
import { fulfillmentStatusWhatsAppMessage, paymentStatusWhatsAppMessage } from '../utils/orderStatusMessages';
import { paymentUrl, whatsappUrl } from '../utils/posOrder';
import { STOREFRONT_URL } from '../utils/storefront';
import OrderInvoiceCard from './OrderInvoiceCard';
import OrderProductionDetails from './OrderProductionDetails';
import RecordPaymentModal from './RecordPaymentModal';
import ShipmentPanel from './ShipmentPanel';
import Modal from './ui/Modal';
import DeliveryAddressFields from './DeliveryAddressFields';
import TemplateActions from './whatsapp/TemplateActions';

// record_payment (backend) also accepts a payment starting from these two statuses, not just
// partially_paid - the summary line and button must be reachable for them too.
const RECORDABLE_PAYMENT_STATUSES = ['awaiting_payment', 'partially_paid', 'failed'];

/** @param {string} eventType @returns {{ icon: any, color: string }} */
const getEventStyle = (eventType) => {
  const map = {
    order_created: { icon: ShoppingBag, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50' },
    payment_received: {
      icon: CreditCard,
      color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/50',
    },
    payment_failed: { icon: AlertCircle, color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50' },
    payment_expired: { icon: Clock, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/50' },
    order_confirmed: {
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/50',
    },
    order_processing: { icon: Package, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50' },
    order_shipped: { icon: Truck, color: 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50' },
    order_delivered: {
      icon: CheckCheck,
      color: 'text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/40',
    },
    order_cancelled: { icon: XCircle, color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50' },
    refund_initiated: { icon: RotateCcw, color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50' },
    refund_completed: {
      icon: CheckCircle,
      color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/50',
    },
    refund_failed: { icon: AlertTriangle, color: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/50' },
    status_changed: { icon: RefreshCw, color: 'text-muted-foreground bg-muted' },
    notes_added: { icon: FileText, color: 'text-muted-foreground bg-muted' },
    stock_restored: { icon: RotateCcw, color: 'text-muted-foreground bg-muted' },
  };
  return map[eventType] || { icon: RefreshCw, color: 'text-muted-foreground bg-muted' };
};

/** The "-" handover placeholder is not a real address part. */
const realValue = (v) => (v && v !== '-' ? v : '');

const OrderDetailModal = ({ orderId, onClose, onUpdate }) => {
  const toast = useToast();
  /** @type {[Order | null, Function]} */
  const [order, setOrder] = useState(null);
  /** @type {[OrderTransaction[], Function]} */
  const [transactions, setTransactions] = useState([]);
  /** @type {[OrderEvent[], Function]} */
  const [events, setEvents] = useState([]);
  /** @type {[import('../types/api').OrderPayment[], Function]} */
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [savingCod, setSavingCod] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [sendingPaymentEmail, setSendingPaymentEmail] = useState(false);
  const [sendingFulfillmentEmail, setSendingFulfillmentEmail] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [error, setError] = useState('');

  // Customer/shipping details editing
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState(null);
  const [savingCustomer, setSavingCustomer] = useState(false);

  // Line-item editing - re-synced from the order every time it (re)loads, so it's always
  // showing the real saved values until the admin starts changing something.
  const [itemEdits, setItemEdits] = useState({});
  const [savingItemId, setSavingItemId] = useState(null);
  const [addQuery, setAddQuery] = useState('');
  const [addResults, setAddResults] = useState([]);
  const [addingItem, setAddingItem] = useState(false);

  const loadOrderDetails = useCallback(async () => {
    try {
      setLoading(true);
      const orderData = await adminOrderService.getOrder(orderId);
      setOrder(orderData);
      setSelectedStatus(orderData.order_status);

      const [transData, eventsData, paymentsData] = await Promise.allSettled([
        adminOrderService.getOrderTransactions(orderId),
        adminOrderService.getOrderEvents(orderId),
        adminOrderService.getOrderPayments(orderId),
      ]);
      if (transData.status === 'fulfilled') setTransactions(transData.value);
      if (eventsData.status === 'fulfilled') setEvents(eventsData.value);
      if (paymentsData.status === 'fulfilled') setPayments(paymentsData.value);
    } catch (err) {
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId, loadOrderDetails]);

  useEffect(() => {
    if (order?.items) {
      setItemEdits(
        Object.fromEntries(order.items.map((i) => [i.id, { quantity: i.quantity, unit_price: Number(i.price) }])),
      );
    }
  }, [order]);

  // Dirty whenever the dropdown differs from the order's actual saved status
  // — a derived comparison, not accumulated state, since this is the only
  // editable field in this modal.
  const isDirty = !!order && selectedStatus !== order.order_status;

  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleUpdateStatus = async () => {
    setUpdating(true);
    try {
      await adminOrderService.updateOrderStatus(orderId, {
        order_status: selectedStatus,
      });
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = async () => {
    const plan = cancelOrderPlan(order);
    if (plan.alreadyCancelled) {
      toast.error(plan.confirmMessage);
      return;
    }
    if (!window.confirm(plan.confirmMessage)) return;
    setCancelling(true);
    try {
      await adminOrderService.updateOrderStatus(orderId, plan.payload);
      toast.success(`Order ${order.order_number} cancelled`);
      onUpdate();
      await loadOrderDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel the order');
    } finally {
      setCancelling(false);
    }
  };

  // Always sends whatever message matches the order's CURRENT status - never a free choice (see
  // utils/orderStatusMessages.js). To send a different message, change the real status first.
  const handleSendPaymentStatusEmail = async () => {
    setSendingPaymentEmail(true);
    try {
      const result = await adminOrderService.sendPaymentStatusEmail(orderId);
      toast.success(`Message emailed to ${result.recipient}`);
      await loadOrderDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to send the email');
    } finally {
      setSendingPaymentEmail(false);
    }
  };

  const handleSendFulfillmentStatusEmail = async () => {
    setSendingFulfillmentEmail(true);
    try {
      const result = await adminOrderService.sendFulfillmentStatusEmail(orderId);
      toast.success(`Message emailed to ${result.recipient}`);
      await loadOrderDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to send the email');
    } finally {
      setSendingFulfillmentEmail(false);
    }
  };

  // Only reachable for orders the backend actually allows deleting (no invoice, no real gateway
  // payment) - it rejects anything else with a clear reason, which the toast surfaces as-is.
  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete order ${order.order_number}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminOrderService.deleteOrder(orderId);
      toast.success(`Order ${order.order_number} deleted`);
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.message || 'Could not delete this order');
    } finally {
      setDeleting(false);
    }
  };

  const startEditCustomer = () => {
    setCustomerForm({
      shipping_first_name: order.shipping_first_name || '',
      shipping_last_name: order.shipping_last_name || '',
      shipping_phone: order.shipping_phone || '',
      recipient_email: order.recipient_email || '',
      shipping_address_line1: order.shipping_address_line1 || '',
      shipping_address_line2: order.shipping_address_line2 || '',
      // A pop-up handover is saved with "-" placeholders; show them empty. Its state is the event's,
      // which is what the invoice already used as its place of supply.
      shipping_city: realValue(order.shipping_city),
      shipping_state: realValue(order.shipping_state) || order.event_state || '',
      shipping_postal_code: realValue(order.shipping_postal_code),
      shipping_country: order.shipping_country || 'India',
    });
    setEditingCustomer(true);
  };

  const handleSaveCustomer = async () => {
    setSavingCustomer(true);
    try {
      // Blank city/state/PIN would overwrite the "-" handover placeholder with nothing (place_of_supply
      // keys off "-"), so only send them once they hold a real value.
      const payload = Object.fromEntries(
        Object.entries(customerForm).filter(
          ([key, value]) => value !== '' || !['shipping_city', 'shipping_state', 'shipping_postal_code'].includes(key),
        ),
      );
      await adminOrderService.updateCustomerDetails(orderId, payload);
      toast.success('Customer details updated');
      setEditingCustomer(false);
      await loadOrderDetails();
    } catch (err) {
      toast.error(err.message || 'Failed to update customer details');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleRemoveItem = async (item) => {
    if (order.items.length <= 1) {
      toast.error('An order needs at least one piece — delete the whole order instead');
      return;
    }
    if (!window.confirm(`Remove ${item.product_name} from this order?`)) return;
    setSavingItemId(item.id);
    try {
      await adminOrderService.removeOrderItem(orderId, item.id);
      toast.success(`Removed ${item.product_name}`);
      await loadOrderDetails();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to remove that piece');
    } finally {
      setSavingItemId(null);
    }
  };

  const handleSaveItem = async (item) => {
    const edit = itemEdits[item.id];
    setSavingItemId(item.id);
    try {
      await adminOrderService.updateOrderItem(orderId, item.id, {
        quantity: edit.quantity,
        unit_price: edit.unit_price,
      });
      toast.success('Piece updated');
      await loadOrderDetails();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to update that piece');
    } finally {
      setSavingItemId(null);
    }
  };

  const searchAddVariants = async (query) => {
    setAddQuery(query);
    if (!query.trim()) {
      setAddResults([]);
      return;
    }
    try {
      const { items: results } = await adminInventoryService.getAllVariants({ search: query, limit: 10 });
      setAddResults(results || []);
    } catch {
      toast.error('Failed to search products');
    }
  };

  const handleAddItem = async (variant) => {
    setAddingItem(true);
    try {
      await adminOrderService.addOrderItem(orderId, {
        product_id: variant.product_id,
        variant_id: variant.variant_id,
        quantity: 1,
      });
      toast.success(`Added ${variant.product_name}`);
      setAddQuery('');
      setAddResults([]);
      await loadOrderDetails();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to add that piece');
    } finally {
      setAddingItem(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      confirmed: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      processing: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      shipped: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      delivered: 'bg-green-200 dark:bg-green-900/60 text-green-900 dark:text-green-300',
      cancelled: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  if (loading) {
    return (
      <Modal maxWidth="sm" fullHeight={false} closeOnBackdrop={false} className="p-6">
        Loading order details...
      </Modal>
    );
  }

  if (error || !order) {
    return (
      <Modal maxWidth="sm" fullHeight={false} closeOnBackdrop={false} className="p-6">
        <p className="text-red-600 dark:text-red-400">{error || 'Order not found'}</p>
        <button onClick={onClose} className="mt-4 px-4 py-2 bg-primary text-primary-foreground">
          Close
        </button>
      </Modal>
    );
  }

  // Same stable URL the POS payment-link flow already uses (pay_path) - visiting it later, once
  // a balance is due, charges whatever is currently owed (see backend's expected_razorpay_charge).
  const balancePaymentLink = paymentUrl(STOREFRONT_URL, `/order-confirmation/${order.id}?pay=true`);
  // Always the message matching the order's CURRENT status - never a free choice (see
  // utils/orderStatusMessages.js). null when that status has no customer message defined.
  const paymentMessage = paymentStatusWhatsAppMessage(order, balancePaymentLink);
  const fulfillmentMessage = fulfillmentStatusWhatsAppMessage(order);
  const canUsePaymentLink = RECORDABLE_PAYMENT_STATUSES.includes(order.payment_status);

  // COD: domestic only, agreed before the shipment is booked; once shipped, record the cash the courier collected.
  const isDomestic = ['IN', 'INDIA'].includes((order.shipping_country || '').trim().toUpperCase());
  const codVisible =
    isDomestic &&
    Number(order.balance_due ?? 0) > 0 &&
    canUsePaymentLink &&
    (order.balance_on_delivery || ['pending', 'confirmed', 'processing'].includes(order.order_status));
  const handleToggleCod = async (enabled) => {
    setSavingCod(true);
    try {
      await adminOrderService.setBalanceOnDelivery(order.id, enabled);
      toast.success(enabled ? 'Balance will be collected on delivery' : 'Collect-on-delivery removed');
      await loadOrderDetails();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to update collect-on-delivery');
    } finally {
      setSavingCod(false);
    }
  };
  const handleCodCollected = async () => {
    setSavingCod(true);
    try {
      await adminOrderService.recordPayment(order.id, { amount: order.balance_due, method: 'cod' });
      toast.success('COD payment recorded');
      await loadOrderDetails();
      onUpdate();
    } catch (err) {
      toast.error(err.message || 'Failed to record the COD payment');
    } finally {
      setSavingCod(false);
    }
  };

  return (
    <Modal maxWidth="4xl" onClose={handleCloseClick} closeOnBackdrop={false} hasUnsavedChanges={isDirty}>
      <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif">Order Details</h2>
          <p className="text-sm text-muted-foreground mt-1">{order.order_number}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            disabled={cancelling || order.order_status === 'cancelled'}
            title="Cancel this order (restores stock; refunds automatically if it was paid through a real payment gateway)"
            className="text-muted-foreground/70 hover:text-amber-600 dark:hover:text-amber-400 disabled:opacity-50"
          >
            <Ban size={20} />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Permanently delete this order (only possible with no invoice and no real gateway payment)"
            className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
          >
            <Trash2 size={20} />
          </button>
          <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
            <X size={24} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Order Status Section */}
        <div className="bg-muted/50 border rounded-lg border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <Package size={20} />
              Order Status
            </h3>
            <div className="flex gap-2">
              <span className={`px-3 py-1 text-sm font-medium ${getStatusColor(order.order_status)}`}>
                {order.order_status}
              </span>
              <span className={`px-3 py-1 text-sm font-medium ${getStatusColor(order.payment_status)}`}>
                {order.payment_status}
              </span>
            </div>
          </div>

          {RECORDABLE_PAYMENT_STATUSES.includes(order.payment_status) && (
            <div className="border-t border-border pt-3 mt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground/80">
                  Total {'₹'}
                  {Number(order.total).toLocaleString('en-IN')} &middot; Paid {'₹'}
                  {Number(order.amount_paid).toLocaleString('en-IN')} &middot; Balance due {'₹'}
                  {Number(order.balance_due).toLocaleString('en-IN')}
                </p>
                {order.balance_due > 0 && (
                  <button
                    onClick={() => setRecordingPayment(true)}
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                  >
                    Record a payment
                  </button>
                )}
              </div>
              {order.balance_due < 0 && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  Overpaid by {'₹'}
                  {Math.abs(Number(order.balance_due)).toLocaleString('en-IN')} - likely because the order was edited (a
                  piece removed, or a bigger discount added) after this payment was recorded. Refund the difference by
                  hand; there is no automatic way to do this.
                </p>
              )}
            </div>
          )}

          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Update Order Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <button
              onClick={handleUpdateStatus}
              disabled={updating || selectedStatus === order.order_status}
              data-testid="update-order-status-btn"
              className="px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50"
            >
              {updating ? 'Updating...' : 'Update Status'}
            </button>
          </div>

          {fulfillmentMessage && (
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground mr-1">Notify customer:</span>
              <a
                href={whatsappUrl(order.shipping_phone_country_code, order.shipping_phone, fulfillmentMessage)}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50"
              >
                Send via WhatsApp
              </a>
              <button
                onClick={handleSendFulfillmentStatusEmail}
                disabled={sendingFulfillmentEmail}
                className="px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
              >
                {sendingFulfillmentEmail ? 'Sending…' : 'Email'}
              </button>
            </div>
          )}
        </div>

        {/* Customer Information */}
        <div className="border rounded-lg border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <User size={20} />
              {order.order_type && order.order_type !== 'standard' ? 'Recipient Information' : 'Customer Information'}
            </h3>
            {!editingCustomer && (
              <button
                onClick={startEditCustomer}
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <Pencil size={14} /> Edit
              </button>
            )}
          </div>

          {editingCustomer ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <input
                  placeholder="First name"
                  value={customerForm.shipping_first_name}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, shipping_first_name: e.target.value }))}
                  className="px-3 py-2 border border-input rounded-lg"
                />
                <input
                  placeholder="Last name"
                  value={customerForm.shipping_last_name}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, shipping_last_name: e.target.value }))}
                  className="px-3 py-2 border border-input rounded-lg"
                />
                <input
                  placeholder="Phone"
                  value={customerForm.shipping_phone}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, shipping_phone: e.target.value }))}
                  className="px-3 py-2 border border-input rounded-lg"
                />
                <input
                  placeholder="Email"
                  value={customerForm.recipient_email}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, recipient_email: e.target.value }))}
                  className="px-3 py-2 border border-input rounded-lg"
                />
                <input
                  placeholder="Address line 1"
                  value={customerForm.shipping_address_line1}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, shipping_address_line1: e.target.value }))}
                  className="col-span-2 px-3 py-2 border border-input rounded-lg"
                />
                <input
                  placeholder="Address line 2"
                  value={customerForm.shipping_address_line2}
                  onChange={(e) => setCustomerForm((f) => ({ ...f, shipping_address_line2: e.target.value }))}
                  className="col-span-2 px-3 py-2 border border-input rounded-lg"
                />
                <DeliveryAddressFields
                  form={customerForm}
                  onChange={(patch) => setCustomerForm((f) => ({ ...f, ...patch }))}
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSaveCustomer}
                  disabled={savingCustomer}
                  className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                >
                  {savingCustomer ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingCustomer(false)}
                  disabled={savingCustomer}
                  className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">
                  {[order.shipping_first_name, order.shipping_last_name].filter(Boolean).join(' ') || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                {/* Gift/sample orders have no linked Customer — fall back to the recipient
                    email captured on the order itself, which is what create_gift_order stores. */}
                <p className="font-medium">
                  {order.customer_email && order.customer_email !== 'N/A'
                    ? order.customer_email
                    : order.recipient_email || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Phone</p>
                <p className="font-medium">{order.shipping_phone || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-muted-foreground">Shipping Address</p>
                <p className="font-medium">
                  {order.shipping_address_line1}
                  {order.shipping_address_line2 && `, ${order.shipping_address_line2}`}
                  <br />
                  {order.shipping_city}, {order.shipping_state} {order.shipping_postal_code}
                  <br />
                  {order.shipping_country}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Shipments */}
        <ShipmentPanel
          orderId={orderId}
          isInternational={
            !!order.shipping_country && !['IN', 'INDIA'].includes(String(order.shipping_country).toUpperCase())
          }
          onOrderChanged={() => {
            loadOrderDetails();
            onUpdate();
          }}
        />

        {/* Order Items */}
        <div className="border rounded-lg border-border p-4">
          <h3 className="text-lg font-medium mb-4">
            Order Items{' '}
            <span className="text-xs font-normal text-muted-foreground/70">
              (editable until this order has a tax invoice)
            </span>
          </h3>
          <div className="space-y-2">
            {order.items && order.items.length > 0 ? (
              order.items.map((item) => {
                const edit = itemEdits[item.id] || { quantity: item.quantity, unit_price: Number(item.price) };
                const dirty = edit.quantity !== item.quantity || edit.unit_price !== Number(item.price);
                const busy = savingItemId === item.id;
                return (
                  <div key={item.id} className="flex flex-wrap items-center gap-2 py-2 border-b last:border-b-0">
                    <div className="flex-1 min-w-[140px]">
                      <p className="font-medium">{item.product_name || item.product_id}</p>
                      <p className="text-xs text-muted-foreground">Size {item.size}</p>
                    </div>
                    <label className="flex items-center gap-1 text-xs text-muted-foreground">
                      Qty
                      <input
                        type="number"
                        min={1}
                        value={edit.quantity}
                        disabled={busy}
                        onChange={(e) =>
                          setItemEdits((prev) => ({
                            ...prev,
                            [item.id]: { ...edit, quantity: Math.max(1, Number(e.target.value) || 1) },
                          }))
                        }
                        className="w-16 px-2 py-1 text-sm border border-input rounded-lg"
                      />
                    </label>
                    <label className="flex items-center gap-1 text-xs text-muted-foreground">
                      {'\u20B9'}
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={edit.unit_price}
                        disabled={busy}
                        onChange={(e) =>
                          setItemEdits((prev) => ({
                            ...prev,
                            [item.id]: { ...edit, unit_price: Math.max(0, Number(e.target.value) || 0) },
                          }))
                        }
                        className="w-24 px-2 py-1 text-sm border border-input rounded-lg"
                      />
                    </label>
                    {dirty && (
                      <button
                        onClick={() => handleSaveItem(item)}
                        disabled={busy}
                        className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
                      >
                        Save
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveItem(item)}
                      disabled={busy}
                      className="text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-muted-foreground text-sm">No items found</p>
            )}
          </div>

          <div className="mt-3 relative">
            <input
              type="text"
              value={addQuery}
              onChange={(e) => searchAddVariants(e.target.value)}
              placeholder="Search products by name or SKU to add a piece..."
              disabled={addingItem}
              className="w-full px-3 py-2 text-sm border border-input rounded-lg disabled:opacity-50"
            />
            {addResults.length > 0 && (
              <div className="border border-border rounded-lg mt-1 max-h-40 overflow-y-auto">
                {addResults.map((v) => (
                  <button
                    key={v.variant_id}
                    onClick={() => handleAddItem(v)}
                    disabled={addingItem || v.stock_quantity <= 0}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 disabled:opacity-40 flex justify-between"
                  >
                    <span>
                      {v.product_name} \u2014 Size {v.size}
                      {v.color ? ` (${v.color})` : ''}
                    </span>
                    <span className="text-muted-foreground/70">{v.stock_quantity} in stock</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Made-to-order details: dispatch date and customer measurements */}
        <OrderProductionDetails key={`${order.id}-${order.updated_at}`} order={order} onSaved={loadOrderDetails} />

        {/* GST tax invoice for this order */}
        <OrderInvoiceCard order={order} />

        {payments.length > 0 && (
          <div className="border rounded-lg border-border p-4">
            <h3 className="text-lg font-medium flex items-center gap-2 mb-3">
              <IndianRupee size={20} />
              Payments Recorded
            </h3>
            <div className="space-y-2 text-sm">
              {payments.map((p) => (
                <div key={p.id} className="flex justify-between border-b border-border/60 pb-2 last:border-0">
                  <span>
                    {'₹'}
                    {Number(p.amount).toLocaleString('en-IN')} &middot; {p.method}
                    {p.reference ? ` (${p.reference})` : ''}
                  </span>
                  <span className="text-muted-foreground">{new Date(p.recorded_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="border rounded-lg border-border p-4">
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <IndianRupee size={20} />
            Order Summary
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Initial price</span>
              <span>
                {'\u20B9'}
                {Number(order.subtotal || order.total).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Discount
                {(order.discount_code || order.discount_reason) && (
                  <span className="text-muted-foreground/70"> ({order.discount_code || order.discount_reason})</span>
                )}
              </span>
              <span>
                {Number(order.discount_amount || 0) > 0 ? '\u2212' : ''}
                {'\u20B9'}
                {Number(order.discount_amount || 0).toLocaleString('en-IN')}
              </span>
            </div>
            {Number(order.shipping_cost || 0) > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shipping</span>
                <span>
                  {'\u20B9'}
                  {Number(order.shipping_cost).toLocaleString('en-IN')}
                </span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t font-medium text-base">
              <span>Subtotal</span>
              <span>
                {'\u20B9'}
                {Number(order.total).toLocaleString('en-IN')}
              </span>
            </div>
            {order.tax_breakdown ? (
              <div className="pl-3 space-y-1 text-xs text-muted-foreground">
                {[
                  { label: 'Taxable value', amount: order.tax_breakdown.taxable_value },
                  ...(order.tax_breakdown.is_intra_state
                    ? [
                        { label: 'CGST', amount: order.tax_breakdown.cgst },
                        { label: 'SGST', amount: order.tax_breakdown.sgst },
                      ]
                    : [{ label: 'IGST', amount: order.tax_breakdown.igst }]),
                ].map(({ label, amount }) => (
                  <div key={label} className="flex justify-between">
                    <span>{label}</span>
                    <span>
                      {'\u20B9'}
                      {Number(amount).toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              Number(order.tax || 0) > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground pl-3">
                  <span>Tax</span>
                  <span>
                    {'\u20B9'}
                    {Number(order.tax).toLocaleString('en-IN')}
                  </span>
                </div>
              )
            )}
            <div className="flex justify-between pt-2 border-t">
              <span className="text-muted-foreground">Paid amount</span>
              <span>
                {'\u20B9'}
                {Number(order.amount_paid ?? 0).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Balance amount</span>
              <span className={Number(order.balance_due ?? 0) < 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                {'\u20B9'}
                {Number(order.balance_due ?? 0).toLocaleString('en-IN')}
              </span>
            </div>
            {Number(order.balance_due ?? 0) < 0 && (
              <p className="text-xs text-red-600 dark:text-red-400">
                Overpaid by {'\u20B9'}
                {Math.abs(Number(order.balance_due)).toLocaleString('en-IN')} - likely because the order was edited (a
                piece removed, or a bigger discount added) after this payment was recorded. Refund the difference by
                hand; there is no automatic way to do this.
              </p>
            )}
            {Number(order.balance_due ?? 0) > 0 && canUsePaymentLink && (
              <button
                onClick={() => setRecordingPayment(true)}
                className="w-full mt-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Record a payment
              </button>
            )}
            {codVisible && (
              <div className="mt-1 p-3 border border-input rounded-lg space-y-2">
                {order.balance_on_delivery ? (
                  <>
                    <p className="text-sm">
                      Balance of {'\u20B9'}
                      {Number(order.balance_due).toLocaleString('en-IN')} will be collected by the courier at delivery.
                    </p>
                    {['shipped', 'delivered'].includes(order.order_status) ? (
                      <button
                        onClick={handleCodCollected}
                        disabled={savingCod}
                        className="w-full px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                      >
                        {savingCod ? 'Recording…' : 'Record COD collected'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleToggleCod(false)}
                        disabled={savingCod}
                        className="w-full px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
                      >
                        Remove collect-on-delivery
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => handleToggleCod(true)}
                    disabled={savingCod}
                    className="w-full px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
                  >
                    Collect balance on delivery (COD)
                  </button>
                )}
              </div>
            )}
            {(canUsePaymentLink || paymentMessage) && (
              <div className="flex gap-2 mt-1">
                {canUsePaymentLink && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(balancePaymentLink);
                      toast.success('Payment link copied');
                    }}
                    className="flex-1 px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50"
                  >
                    Copy payment link
                  </button>
                )}
                {paymentMessage && (
                  <a
                    href={whatsappUrl(order.shipping_phone_country_code, order.shipping_phone, paymentMessage)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 text-center"
                  >
                    Send via WhatsApp
                  </a>
                )}
              </div>
            )}
            {paymentMessage && (
              <button
                onClick={handleSendPaymentStatusEmail}
                disabled={sendingPaymentEmail}
                className="w-full mt-1 px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
              >
                {sendingPaymentEmail ? 'Sending…' : 'Email current status to customer'}
              </button>
            )}
            <TemplateActions
              scope="orders"
              id={order.id}
              className="mt-1"
              excludeKinds={[
                ...(Number(order.balance_due ?? 0) > 0 && canUsePaymentLink ? [] : ['balance_payment_link']),
                ...(order.balance_on_delivery ? [] : ['balance_on_delivery', 'delivery_attempt_failed']),
              ]}
            />
          </div>
        </div>

        {/* Transactions */}
        {transactions.length > 0 && (
          <div className="border rounded-lg border-border p-4">
            <h3 className="text-lg font-medium mb-4">Transaction History</h3>
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex justify-between items-center py-2 border-b last:border-b-0 text-sm"
                >
                  <div>
                    <p className="font-medium">{transaction.payment_gateway}</p>
                    <p className="text-muted-foreground">{formatDate(transaction.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">
                      {'\u20B9'}
                      {Number(transaction.amount).toLocaleString('en-IN')}
                    </p>
                    <span className={`text-xs px-2 py-1 ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Timeline */}
        <div className="border rounded-lg border-border p-4">
          <h3 className="text-lg font-medium mb-4">Order Timeline</h3>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground/70">No events recorded yet.</p>
          ) : (
            <ol className="relative border-l border-border ml-3 space-y-0">
              {events.map((event, idx) => {
                const { icon: Icon, color } = getEventStyle(event.event_type);
                const isLast = idx === events.length - 1;
                return (
                  <li key={event.id} className={`ml-6 ${isLast ? 'pb-0' : 'pb-5'}`}>
                    <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ${color}`}>
                      <Icon size={13} />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground leading-snug">{event.summary}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <time className="text-xs text-muted-foreground/70">{formatDate(event.created_at)}</time>
                        {event.actor_name && (
                          <span className="text-xs text-muted-foreground/70">· {event.actor_name}</span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded-md ${color} font-medium`}>
                          {event.actor_type}
                        </span>
                      </div>
                      {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <details className="mt-1">
                          <summary className="text-xs text-muted-foreground/70 cursor-pointer select-none">
                            details
                          </summary>
                          <pre className="text-xs text-muted-foreground mt-1 bg-muted/50 p-2 overflow-x-auto">
                            {JSON.stringify(event.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Order Metadata */}
        <div className="text-xs text-muted-foreground pt-4 border-t">
          <p>Order ID: {order.id}</p>
          <p>Created: {formatDate(order.created_at)}</p>
          {order.updated_at && <p>Last Updated: {formatDate(order.updated_at)}</p>}
        </div>
      </div>

      {recordingPayment && (
        <RecordPaymentModal
          orderId={order.id}
          balanceDue={order.balance_due}
          onClose={() => setRecordingPayment(false)}
          onRecorded={() => {
            setRecordingPayment(false);
            loadOrderDetails();
            onUpdate();
          }}
        />
      )}
    </Modal>
  );
};

export default OrderDetailModal;
